import {
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import useListLocationDetails from '../../dataFetchHooks/useListLocationDetails'
import useListLocationMetricValues from '../../dataFetchHooks/useListLocationMetricValues'
import { IMetricValue, IQueryFilter } from '../../type'

interface IAiBlackboxProps {
  startDate?: string
  endDate?: string
  queryFilter: IQueryFilter | null
}

type Order = 'asc' | 'desc'
type OrderBy = 'location' | 'net_sales' | 'check_count' | 'check_average'

interface LocationMetricRow {
  locationId: number
  locationCode: string
  locationName: string
  netSales: IMetricValue | null
  checkCount: IMetricValue | null
  checkAverage: IMetricValue | null
}

const TableExample: React.FC<IAiBlackboxProps> = ({
  startDate = '',
  endDate = '',
  queryFilter,
}) => {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<OrderBy>('location')
  const [page, setPage] = useState(1)
  const rowsPerPage = 20

  // Fetch location details
  const { data: locationsMap, isLoading: isLoadingLocations } =
    useListLocationDetails()

  // Fetch metric values
  const metricCodes = ['net_sales', 'check_count', 'check_average']
  const { data: metricsData, isLoading: isLoadingMetrics } =
    useListLocationMetricValues(
      startDate,
      endDate,
      queryFilter || { locationIds: [] },
      metricCodes,
    )

  // Reset pagination when data changes
  useEffect(() => {
    setPage(1)
  }, [startDate, endDate, queryFilter])

  // Prepare table data
  const tableData = useMemo(() => {
    if (!metricsData?.locations || !locationsMap) {
      return []
    }

    return metricsData.locations
      .map((location) => {
        const locationDetail = locationsMap[location.locationId] || {
          code: '',
          name: '',
        }

        return {
          locationId: location.locationId,
          locationCode: locationDetail?.code || '',
          locationName: locationDetail?.name || '',
          netSales: location.metricData?.net_sales || null,
          checkCount: location.metricData?.check_count || null,
          checkAverage: location.metricData?.check_average || null,
        }
      })
      .filter(
        (row) =>
          // Filter out rows where all metric values are null or undefined
          row.netSales !== null ||
          row.checkCount !== null ||
          row.checkAverage !== null,
      )
  }, [metricsData, locationsMap])

  // Calculate totals
  const totals = useMemo(() => {
    // Use summary data from the hook if available
    if (metricsData?.summary) {
      return {
        netSalesTotal: metricsData.summary.net_sales?.value ?? 0,
        checkCountTotal: metricsData.summary.check_count?.value ?? 0,
        checkAverageTotal: metricsData.summary.check_average?.value ?? 0,
      }
    }

    // Fallback to manual calculation if summary is not available
    if (!tableData || tableData.length === 0) {
      return {
        netSalesTotal: 0,
        checkCountTotal: 0,
        checkAverageTotal: 0,
      }
    }

    const netSalesTotal = tableData.reduce(
      (sum, row) => sum + (row.netSales?.value ?? 0),
      0,
    )

    const checkCountTotal = tableData.reduce(
      (sum, row) => sum + (row.checkCount?.value ?? 0),
      0,
    )

    // Calculate the overall check average
    const checkAverageTotal =
      checkCountTotal > 0 ? netSalesTotal / checkCountTotal : 0

    return {
      netSalesTotal,
      checkCountTotal,
      checkAverageTotal,
    }
  }, [tableData, metricsData])

  // Handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedData = useMemo(() => {
    if (tableData.length === 0) {
      return []
    }

    return [...tableData].sort((a, b) => {
      let comparison = 0

      if (orderBy === 'location') {
        const aLocation = `${a.locationCode} - ${a.locationName}`
        const bLocation = `${b.locationCode} - ${b.locationName}`
        comparison = aLocation.localeCompare(bLocation)
      } else if (orderBy === 'net_sales') {
        comparison = (a.netSales?.value ?? 0) - (b.netSales?.value ?? 0)
      } else if (orderBy === 'check_count') {
        comparison = (a.checkCount?.value ?? 0) - (b.checkCount?.value ?? 0)
      } else if (orderBy === 'check_average') {
        comparison = (a.checkAverage?.value ?? 0) - (b.checkAverage?.value ?? 0)
      }

      return order === 'asc' ? comparison : -comparison
    })
  }, [tableData, order, orderBy])

  // Handle pagination
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, page, rowsPerPage])

  // Format number with commas for thousands
  const formatNumber = (
    value: number | undefined,
    isCurrency: boolean = false,
    decimals?: number,
  ) => {
    if (value === undefined || value === null) return '-'

    // Determine decimal places based on the PRD requirements
    let decimalPlaces = decimals
    if (isCurrency && decimalPlaces === undefined) {
      decimalPlaces = Math.abs(value) < 1000 ? 2 : 0
    }

    const formattedNumber = value.toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces ?? 2,
      maximumFractionDigits: decimalPlaces ?? 2,
    })

    return isCurrency ? `$${formattedNumber}` : formattedNumber
  }

  // Add theme and media query for responsive design
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (isLoadingLocations || isLoadingMetrics) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!metricsData || !locationsMap || tableData.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body1">
          No data available for the selected filters.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      p={isMobile ? 1 : 1.5}
      sx={{
        fontFamily: 'Lexend-Regular',
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: '1000px',
          margin: isMobile ? '0 auto' : '0',
          overflowX: 'auto',
        }}
      >
        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}
        >
          <Table
            size="small"
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              tableLayout: 'fixed',
              minWidth: '650px',
              '& .MuiTableCell-root': {
                padding: isMobile ? '6px 8px' : '8px 12px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: isMobile ? '30%' : '40%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'location'}
                    direction={orderBy === 'location' ? order : 'asc'}
                    onClick={() => handleRequestSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '23%' : '20%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'net_sales'}
                    direction={orderBy === 'net_sales' ? order : 'asc'}
                    onClick={() => handleRequestSort('net_sales')}
                  >
                    Net Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '23%' : '20%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'check_count'}
                    direction={orderBy === 'check_count' ? order : 'asc'}
                    onClick={() => handleRequestSort('check_count')}
                  >
                    Check Count
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '24%' : '20%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'check_average'}
                    direction={orderBy === 'check_average' ? order : 'asc'}
                    onClick={() => handleRequestSort('check_average')}
                  >
                    Check Average
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.locationId}>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 0, // Needed for text-overflow to work
                    }}
                  >
                    {`${row.locationCode} - ${row.locationName}`}
                  </TableCell>
                  <TableCell align="right">
                    {row.netSales
                      ? formatNumber(row.netSales.value, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.checkCount
                      ? formatNumber(row.checkCount.value, false, 0)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.checkAverage
                      ? formatNumber(row.checkAverage.value, true)
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& > *': { fontFamily: 'Lexend-SemiBold' } }}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  Total
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.netSalesTotal, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.checkCountTotal, false, 0)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.checkAverageTotal, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {sortedData.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Pagination
              count={Math.ceil(sortedData.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="small"
              siblingCount={isMobile ? 0 : 1}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default TableExample
